import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Eye, EyeOff, LogIn, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_jax-craft-remodel/artifacts/6r3h6sd1_Transparent%20Logo.png";

export const CustomerLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_URL}/api/customer/login`, formData);
      const { access_token, customer } = response.data;
      
      // Store token and customer data
      localStorage.setItem('customer_token', access_token);
      localStorage.setItem('customer_data', JSON.stringify(customer));
      
      toast.success(`Welcome back, ${customer.name}!`);
      navigate('/customer/project');
    } catch (err) {
      const message = err.response?.data?.detail || 'Invalid email or password';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex flex-col" data-testid="customer-login-page">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23C6A87C' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      {/* Back to Home */}
      <div className="relative z-10 p-6">
        <a 
          href="/"
          className="inline-flex items-center gap-2 text-gray-400 hover:text-[#C6A87C] transition-colors text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Website
        </a>
      </div>

      {/* Login Card */}
      <div className="flex-1 flex items-center justify-center px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Logo */}
          <div className="text-center mb-8">
            <img 
              src={LOGO_URL} 
              alt="Jax Remodeling Solutions" 
              className="h-16 w-auto mx-auto mb-6"
            />
            <h1 className="font-heading text-2xl text-white mb-2">Client Portal</h1>
            <p className="text-gray-400 text-sm">
              Sign in to view your project status
            </p>
          </div>

          {/* Login Form */}
          <div className="bg-white/5 backdrop-blur border border-white/10 p-8">
            {error && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-black/50 border border-white/10 text-white placeholder-gray-500 focus:border-[#C6A87C] focus:outline-none transition-colors"
                  placeholder="your@email.com"
                  data-testid="customer-email-input"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-black/50 border border-white/10 text-white placeholder-gray-500 focus:border-[#C6A87C] focus:outline-none transition-colors pr-12"
                    placeholder="Enter your password"
                    data-testid="customer-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-[#C6A87C] text-white font-semibold uppercase tracking-widest hover:bg-[#B09265] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                data-testid="customer-login-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <LogIn className="w-5 h-5" />
                    Sign In
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-white/10 text-center">
              <p className="text-gray-500 text-sm">
                Don't have an account?{' '}
                <a href="/contact" className="text-[#C6A87C] hover:underline">
                  Contact Us
                </a>
              </p>
            </div>
          </div>

          {/* Info */}
          <p className="text-center text-gray-500 text-xs mt-6">
            Your login credentials were provided by our team.
            <br />
            Contact us if you need assistance.
          </p>
        </motion.div>
      </div>

      {/* Footer */}
      <div className="relative z-10 p-6 text-center">
        <p className="text-gray-600 text-xs">
          &copy; {new Date().getFullYear()} Jax Remodeling Solutions LLC. All rights reserved.
        </p>
      </div>
    </div>
  );
};
